/**
 * useSpeedBoatLiveSession
 *
 * Manages live state for a Speed Boat collaborative session.
 * Same Realtime + event-sourcing approach as useCollaborativeGame, but
 * derives the Speed Boat-specific state: four-category factor board where
 * placement order in the transcript IS the record of the conversation.
 *
 * The sequence in which participants place post-its — who raised what first,
 * what got reclassified, what was removed after discussion — carries signal
 * that a final ranked list cannot.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { getSupabaseClient } from '../lib/supabaseClient';
import {
  getSessionById,
  getSessionEvents,
  recordEvent,
  updateSessionStatus,
} from '../lib/collaborativeGameService';
import type {
  CollaborativeGameSession,
  GameEvent,
  LiveParticipant,
  LiveSpeedBoatFactor,
  SpeedBoatConfig,
  SpeedBoatFactorType,
} from '../types/collaborativeGame';

// Derive the live board state from the ordered event log
function deriveSpeedBoatState(
  session: CollaborativeGameSession,
  events: GameEvent[]
): { participants: LiveParticipant[]; factors: LiveSpeedBoatFactor[] } {
  const config = session.config as SpeedBoatConfig;
  const participantsMap = new Map<string, LiveParticipant>();
  const factorsMap = new Map<string, LiveSpeedBoatFactor>();

  // Seed any initial factors from config
  if (config.initial_factors) {
    for (const f of config.initial_factors) {
      factorsMap.set(f.id, {
        id: f.id,
        name: f.name,
        type: f.type,
        addedBy: session.hostName ?? 'host',
        addedAt: session.createdAt,
        sequenceNum: 0,
      });
    }
  }

  for (const event of events) {
    const name = event.participantName;

    if (event.eventType === 'join') {
      if (!participantsMap.has(name)) {
        const data = event.eventData as { isHost?: boolean };
        participantsMap.set(name, {
          name,
          isHost: data.isHost ?? false,
          isDone: false,
          joinedAt: event.createdAt,
          purchases: [],
          remainingBudget: 0,
        });
      }
    }

    if (event.eventType === 'add_factor') {
      const data = event.eventData as { factorId: string; factorName: string; factorType: SpeedBoatFactorType; note?: string };
      factorsMap.set(data.factorId, {
        id: data.factorId,
        name: data.factorName,
        type: data.factorType,
        addedBy: name,
        addedAt: event.createdAt,
        note: data.note,
        sequenceNum: event.sequenceNum,
      });
    }

    if (event.eventType === 'remove_factor') {
      const data = event.eventData as { factorId: string };
      factorsMap.delete(data.factorId);
    }

    if (event.eventType === 'reclassify_factor') {
      const data = event.eventData as { factorId: string; toType: SpeedBoatFactorType; note?: string };
      const existing = factorsMap.get(data.factorId);
      if (existing) {
        factorsMap.set(data.factorId, { ...existing, type: data.toType });
      }
    }

    if (event.eventType === 'done') {
      const participant = participantsMap.get(name);
      if (participant) participant.isDone = true;
    }
  }

  return {
    participants: Array.from(participantsMap.values()),
    factors: Array.from(factorsMap.values()).sort((a, b) => a.sequenceNum - b.sequenceNum),
  };
}

export interface SpeedBoatLiveSessionState {
  session: CollaborativeGameSession | null;
  participants: LiveParticipant[];
  factors: LiveSpeedBoatFactor[];
  events: GameEvent[];
  isConnected: boolean;
  error: string | null;
}

export interface SpeedBoatLiveSessionActions {
  addFactor: (name: string, type: SpeedBoatFactorType, note?: string) => Promise<void>;
  removeFactor: (factorId: string, factorName: string) => Promise<void>;
  reclassifyFactor: (factorId: string, factorName: string, fromType: SpeedBoatFactorType, toType: SpeedBoatFactorType, note?: string) => Promise<void>;
  addComment: (text: string) => Promise<void>;
  markDone: () => Promise<void>;
  startSession: () => Promise<void>;
  endSession: () => Promise<void>;
}

export type UseSpeedBoatLiveSessionResult = SpeedBoatLiveSessionState & SpeedBoatLiveSessionActions;

export function useSpeedBoatLiveSession(
  sessionId: string | null,
  myName: string
): UseSpeedBoatLiveSessionResult {
  const [session, setSession] = useState<CollaborativeGameSession | null>(null);
  const [events, setEvents] = useState<GameEvent[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const channelRef = useRef<RealtimeChannel | null>(null);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    (async () => {
      try {
        const [sess, evts] = await Promise.all([
          getSessionById(sessionId),
          getSessionEvents(sessionId),
        ]);
        if (cancelled) return;
        setSession(sess);
        setEvents(evts);
        setError(null);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load session');
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    const supabase = getSupabaseClient();
    const channelName = `speedboat-${sessionId}`;

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'collaborative_game_events', filter: `session_id=eq.${sessionId}` },
        (payload) => {
          const row = payload.new as Record<string, unknown>;
          const newEvent: GameEvent = {
            id: row.id as string,
            sessionId: row.session_id as string,
            sequenceNum: row.sequence_num as number,
            participantName: row.participant_name as string,
            eventType: row.event_type as GameEvent['eventType'],
            eventData: (row.event_data as GameEvent['eventData']) || {},
            createdAt: row.created_at as string,
          };
          setEvents(prev => {
            if (prev.find(e => e.id === newEvent.id)) return prev;
            return [...prev, newEvent].sort((a, b) => a.sequenceNum - b.sequenceNum);
          });
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'collaborative_game_sessions', filter: `id=eq.${sessionId}` },
        (payload) => {
          const row = payload.new as Record<string, unknown>;
          setSession(prev => prev ? {
            ...prev,
            status: row.status as CollaborativeGameSession['status'],
            updatedAt: row.updated_at as string,
          } : prev);
        }
      )
      .subscribe((status) => {
        setIsConnected(status === 'SUBSCRIBED');
        if (status === 'CHANNEL_ERROR') setError('Realtime connection error');
      });

    channelRef.current = channel;
    return () => { channel.unsubscribe(); channelRef.current = null; };
  }, [sessionId]);

  const addFactor = useCallback(async (name: string, type: SpeedBoatFactorType, note?: string) => {
    if (!sessionId || !myName) return;
    const factorId = `f-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    await recordEvent(sessionId, myName, 'add_factor', {
      factorId,
      factorName: name.trim(),
      factorType: type,
      ...(note ? { note: note.trim() } : {}),
    });
  }, [sessionId, myName]);

  const removeFactor = useCallback(async (factorId: string, factorName: string) => {
    if (!sessionId || !myName) return;
    await recordEvent(sessionId, myName, 'remove_factor', { factorId, factorName });
  }, [sessionId, myName]);

  const reclassifyFactor = useCallback(async (
    factorId: string,
    factorName: string,
    fromType: SpeedBoatFactorType,
    toType: SpeedBoatFactorType,
    note?: string
  ) => {
    if (!sessionId || !myName) return;
    await recordEvent(sessionId, myName, 'reclassify_factor', {
      factorId,
      factorName,
      fromType,
      toType,
      ...(note ? { note: note.trim() } : {}),
    });
  }, [sessionId, myName]);

  const addComment = useCallback(async (text: string) => {
    if (!sessionId || !myName) return;
    await recordEvent(sessionId, myName, 'comment', { text });
  }, [sessionId, myName]);

  const markDone = useCallback(async () => {
    if (!sessionId || !myName) return;
    await recordEvent(sessionId, myName, 'done', {});
  }, [sessionId, myName]);

  const startSession = useCallback(async () => {
    if (!sessionId || !myName) return;
    await updateSessionStatus(sessionId, 'active');
    await recordEvent(sessionId, myName, 'session_started', {});
  }, [sessionId, myName]);

  const endSession = useCallback(async () => {
    if (!sessionId || !myName) return;
    await updateSessionStatus(sessionId, 'ended');
    await recordEvent(sessionId, myName, 'session_ended', {});
  }, [sessionId, myName]);

  const derived = session
    ? deriveSpeedBoatState(session, events)
    : { participants: [], factors: [] };

  return {
    session,
    events,
    isConnected,
    error,
    ...derived,
    addFactor,
    removeFactor,
    reclassifyFactor,
    addComment,
    markDone,
    startSession,
    endSession,
  };
}
